import type { TypographyVariantsOptions } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import { color, fontFamily, fontSize, fontWeight, lineHeight } from './tokens/primitives';

/**
 * Typography variants from Figma typography page. Mapping:
 *   MUI h1..h4       ← Figma Heading 1..4 (56 / 48 / 36 / 28)
 *   MUI h5..h6       ← Figma Subheading 1..2 (24 / 20)
 *   MUI subtitle1    ← Figma Body 1 / Semibold (16)
 *   MUI subtitle2    ← Figma Body 2 / Medium (14)
 *   MUI body1..2     ← Figma Body 1..2 / Regular (16 / 14)
 *   custom body1Medium ← Figma Body 1 / 16-Medium (16 / 500)
 *                        — used for card titles, list-item headings, etc.
 *   MUI caption      ← Figma Caption (12)
 *
 * Figma "Display lg" (48 / -2 letter-spacing) has no MUI slot and is
 * intentionally deferred. The Storybook foundation story shows it as
 * out-of-scope until a custom variant is added via module augmentation.
 */
declare module '@mui/material/styles' {
  interface TypographyVariants {
    body1Medium: CSSProperties;
    inputLabel: CSSProperties;
    inputHelper: CSSProperties;
    fieldHelper: CSSProperties;
    switchTitle: CSSProperties;
    switchDescription: CSSProperties;
    sectionHeader: CSSProperties;
    textareaValue: CSSProperties;
    comboboxData: CSSProperties;
  }
  interface TypographyVariantsOptions {
    body1Medium?: CSSProperties;
    inputLabel?: CSSProperties;
    inputHelper?: CSSProperties;
    fieldHelper?: CSSProperties;
    switchTitle?: CSSProperties;
    switchDescription?: CSSProperties;
    sectionHeader?: CSSProperties;
    textareaValue?: CSSProperties;
    comboboxData?: CSSProperties;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body1Medium: true;
    inputLabel: true;
    inputHelper: true;
    fieldHelper: true;
    switchTitle: true;
    switchDescription: true;
    sectionHeader: true;
    textareaValue: true;
    comboboxData: true;
  }
}
export const typography: TypographyVariantsOptions = {
  fontFamily: fontFamily.sans,

  htmlFontSize: 16,
  fontSize: 14,

  fontWeightLight: fontWeight.regular,
  fontWeightRegular: fontWeight.regular,
  fontWeightMedium: fontWeight.medium,
  fontWeightBold: fontWeight.bold,

  h1: {
    fontFamily: fontFamily.display,
    fontSize: `${fontSize[56]}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[64],
  },
  h2: {
    fontFamily: fontFamily.display,
    fontSize: `${fontSize[48]}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[57],
  },
  h3: {
    fontFamily: fontFamily.display,
    fontSize: `${fontSize[36]}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[48],
  },
  h4: {
    fontFamily: fontFamily.display,
    fontSize: `${fontSize[28]}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[36],
  },
  h5: {
    fontSize: `${fontSize[24]}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[30],
  },
  h6: {
    fontSize: `${fontSize[20]}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[26],
  },
  subtitle1: {
    fontSize: `${fontSize[16]}px`,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight[24],
  },
  subtitle2: {
    fontSize: `${fontSize[14]}px`,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight[20],
  },
  body1: {
    fontSize: `${fontSize[16]}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[24],
  },
  body1Medium: {
    fontSize: `${fontSize[16]}px`,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight[24],
  },
  /** Field label rendered above an outlined input (Figma 13 / Medium / Pale Sky). */
  inputLabel: {
    fontSize: `${fontSize[13]}px`,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight[20],
  },
  /** Larger hint line, e.g. the drawer subtitle (Figma 12.5 / Regular / Pale Sky 20). */
  inputHelper: {
    fontSize: `${fontSize['12_5']}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[20],
  },
  /** Explanation text under an input field (Figma 11.5 / Regular / Gray Chateau #9CA3AF / 16.1). */
  fieldHelper: {
    fontSize: `${fontSize['11_5']}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight['16_1'],
    color: color.neutrals[450],
  },
  /** Title row of a toggle switch (Figma 13.5 / Medium / Shark). */
  switchTitle: {
    fontSize: `${fontSize['13_5']}px`,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight[20],
  },
  /** Description line under a switch title (Figma 12.5 / Regular / Pale Sky 17.5). */
  switchDescription: {
    fontSize: `${fontSize['12_5']}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight['17_5'],
  },
  /** Section header above a form card (Figma 11.5 / Semibold / 20 / 0.69 letter-spacing / uppercase). */
  sectionHeader: {
    fontSize: `${fontSize['11_5']}px`,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight[20],
    letterSpacing: '0.69px',
    textTransform: 'uppercase' as const,
  },
  /** Textarea value text (Figma 13 / Regular / 20.13). */
  textareaValue: {
    fontSize: `${fontSize[13]}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight['20_13'],
  },
  /** Combobox / dropdown data value (Figma 11 / Medium / 23). */
  comboboxData: {
    fontSize: `${fontSize[11]}px`,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight[23],
  },
  body2: {
    fontSize: `${fontSize[14]}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[20],
  },
  caption: {
    fontSize: `${fontSize[12]}px`,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[16],
  },
  overline: {
    fontSize: `${fontSize[12]}px`,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight[16],
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  button: {
    fontFamily: fontFamily.sans,
    fontSize: `${fontSize[14]}px`,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight[20],
    textTransform: 'none',
  },
};
